import React, { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { api, formatError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common/Bits";
import { Clock, Plus, Trash2, X, Save } from "lucide-react";
import { toast } from "sonner";
import { useAIOutputs } from "@/hooks/useAIOutputs";
import AICategoryPanel from "@/components/ai/AICategoryPanel";

const IMPORTANCES = [
  { v: "low", label: "Χαμηλή" },
  { v: "medium", label: "Μέτρια" },
  { v: "high", label: "Υψηλή" },
  { v: "critical", label: "Κρίσιμη" },
];

export default function Timeline() {
  const { t, currentCase } = useApp();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");
  const [witnesses, setWitnesses] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const { outputs: aiOutputs, addOutput: addAIOutput } = useAIOutputs(currentCase?.id);

  useEffect(() => {
    if (!currentCase) return;
    api.get(`/cases/${currentCase.id}/timeline`).then(r => setItems(r.data)).catch(()=>{});
    api.get(`/cases/${currentCase.id}/witnesses`).then(r => setWitnesses(r.data)).catch(()=>{});
    api.get(`/cases/${currentCase.id}/evidence`).then(r => setEvidence(r.data)).catch(()=>{});
  }, [currentCase]);

  const wName = id => witnesses.find(w => w.id === id)?.full_name;
  const eName = id => { const e = evidence.find(x => x.id === id); return e ? (e.evidence_id || e.title) : null; };

  const toggleLink = (field, id) => {
    const arr = editing[field] || [];
    setEditing({ ...editing, [field]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] });
  };

  const sorted = [...items]
    .filter(i => !filter || i.importance === filter)
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const save = async () => {
    try {
      if (editing.id && items.some(i => i.id === editing.id)) {
        const { data } = await api.patch(`/cases/${currentCase.id}/timeline/${editing.id}`, editing);
        setItems(arr => arr.map(x => x.id === data.id ? data : x));
      } else {
        const { data } = await api.post(`/cases/${currentCase.id}/timeline`, editing);
        setItems(arr => [...arr, data]);
      }
      setEditing(null); toast.success(t("saved"));
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Διαγραφή;")) return;
    await api.delete(`/cases/${currentCase.id}/timeline/${id}`);
    setItems(arr => arr.filter(x => x.id !== id));
  };

  const importanceColor = (imp) => ({
    critical: "bg-red-500", high: "bg-amber-500", medium: "bg-blue-500", low: "bg-slate-500"
  }[imp] || "bg-slate-500");

  return (
    <div data-testid="timeline-page">
      <PageHeader
        title="Χρονολόγιο Γεγονότων"
        subtitle="Διαδραστικό χρονολόγιο με φιλτράρισμα κατά σημαντικότητα."
        action={
          <div className="flex gap-2">
            <a data-testid="timeline-export-pdf-btn"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${currentCase?.id}/timeline/pdf`}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-border/80 hover:border-[hsl(var(--gold)/0.4)] font-medium text-[13px]">
              Εξαγωγή PDF
            </a>
            <button data-testid="timeline-add-btn" onClick={() => setEditing({ importance: "medium" })}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px]">
              <Plus className="size-4"/>{t("add")}
            </button>
          </div>
        }
      />

      <div className="flex gap-2 mb-6">
        <select data-testid="timeline-filter" value={filter} onChange={e => setFilter(e.target.value)}
          className="bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] outline-none">
          <option value="">Όλη η σημαντικότητα</option>
          {IMPORTANCES.map(i => <option key={i.v} value={i.v}>{i.label}</option>)}
        </select>
      </div>

      {sorted.length === 0 ? (
        <EmptyState title={t("empty_state")} subtitle="Προσθέστε το πρώτο γεγονός στο χρονολόγιο." icon={Clock}/>
      ) : (
        <div className="relative pl-8">
          {/* Tracing beam */}
          <div className="absolute left-3 top-0 bottom-0 w-px bg-gradient-to-b from-[hsl(var(--gold)/0.6)] via-border to-transparent" />
          {sorted.map((e, i) => (
            <div key={e.id} className="relative mb-6 animate-fade-in" data-testid={`timeline-item-${e.id}`}>
              <div className={`absolute -left-[22px] top-2 size-3 rounded-full ${importanceColor(e.importance)} ring-4 ring-background`} />
              <div className="surface-card surface-card-hover p-4 cursor-pointer" onClick={() => setEditing(e)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="font-mono text-[11px] gold-text">{e.date || "—"} {e.time || ""}</span>
                      <StatusBadge tone={e.importance}>{IMPORTANCES.find(i => i.v === e.importance)?.label || "—"}</StatusBadge>
                    </div>
                    <h4 className="font-heading font-semibold text-[14px] mb-1">{e.title}</h4>
                    <p className="text-[12.5px] text-muted-foreground leading-relaxed">{e.description}</p>
                    {(e.persons || e.source || (e.witness_ids||[]).length > 0 || (e.evidence_ids||[]).length > 0) && (
                      <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-muted-foreground font-mono">
                        {e.persons && <span>👥 {e.persons}</span>}
                        {e.source && <span>📎 {e.source}</span>}
                        {(e.witness_ids || []).map(id => wName(id) && (
                          <span key={id} className="px-1.5 py-0.5 rounded bg-[hsl(var(--gold)/0.1)] gold-text">⚖ {wName(id)}</span>
                        ))}
                        {(e.evidence_ids || []).map(id => eName(id) && (
                          <span key={id} className="px-1.5 py-0.5 rounded bg-[hsl(var(--gold)/0.1)] gold-text">📦 {eName(id)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={ev => { ev.stopPropagation(); remove(e.id); }} data-testid={`timeline-delete-${e.id}`}
                    className="p-1.5 hover:bg-red-500/10 hover:text-red-400 rounded opacity-50 hover:opacity-100">
                    <Trash2 className="size-3.5"/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6">
        <AICategoryPanel
          caseId={currentCase?.id} promptKey="timeline" titleEl="Χρονολόγιο Γεγονότων (AI)"
          outputs={aiOutputs} onGenerated={addAIOutput}
        />
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="surface-card w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
            <div className="flex items-center justify-between p-5 border-b border-border/60 sticky top-0 bg-card">
              <h3 className="font-heading font-semibold text-[15px]">{editing.id ? t("edit") : t("add")} γεγονότος</h3>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-muted rounded"><X className="size-4"/></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Inp label="Ημερομηνία" type="date" v={(editing.date||"").slice(0,10)} on={v => setEditing({...editing, date: v})}/>
                <Inp label="Ώρα" type="time" v={editing.time} on={v => setEditing({...editing, time: v})}/>
              </div>
              <Inp label="Τίτλος" v={editing.title} on={v => setEditing({...editing, title: v})}/>
              <Inp label="Περιγραφή" textarea v={editing.description} on={v => setEditing({...editing, description: v})}/>
              <div className="grid grid-cols-2 gap-3">
                <Inp label="Πηγή" v={editing.source} on={v => setEditing({...editing, source: v})}/>
                <Inp label="Εμπλεκόμενοι" v={editing.persons} on={v => setEditing({...editing, persons: v})}/>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Σημαντικότητα</label>
                <select value={editing.importance} onChange={e => setEditing({...editing, importance: e.target.value})}
                  className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px]">
                  {IMPORTANCES.map(i => <option key={i.v} value={i.v}>{i.label}</option>)}
                </select>
              </div>
              <Inp label="Tags" v={editing.tags} on={v => setEditing({...editing, tags: v})}/>
              {witnesses.length > 0 && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Συνδεδεμένοι μάρτυρες</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {witnesses.map(w => {
                      const on = (editing.witness_ids || []).includes(w.id);
                      return (
                        <button key={w.id} type="button" onClick={() => toggleLink("witness_ids", w.id)}
                          data-testid={`timeline-link-witness-${w.id}`}
                          className={`px-2.5 py-1 rounded-full border text-[11.5px] transition-colors ${on
                            ? "border-[hsl(var(--gold)/0.6)] bg-[hsl(var(--gold)/0.15)] gold-text"
                            : "border-border/60 text-muted-foreground hover:border-[hsl(var(--gold)/0.3)]"}`}>
                          {w.full_name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {evidence.length > 0 && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Συνδεδεμένα αποδεικτικά</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {evidence.map(ev => {
                      const on = (editing.evidence_ids || []).includes(ev.id);
                      return (
                        <button key={ev.id} type="button" onClick={() => toggleLink("evidence_ids", ev.id)}
                          data-testid={`timeline-link-evidence-${ev.id}`}
                          className={`px-2.5 py-1 rounded-full border text-[11.5px] transition-colors ${on
                            ? "border-[hsl(var(--gold)/0.6)] bg-[hsl(var(--gold)/0.15)] gold-text"
                            : "border-border/60 text-muted-foreground hover:border-[hsl(var(--gold)/0.3)]"}`}>
                          {ev.evidence_id || ev.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-border/60">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-border/80 text-[13px]">{t("cancel")}</button>
              <button onClick={save} data-testid="timeline-modal-save"
                className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px]">
                <Save className="size-4"/>{t("save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Inp({ label, v, on, textarea, type = "text" }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</label>
      {textarea ?
        <textarea value={v || ""} onChange={e => on(e.target.value)} rows={3}
          className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] resize-none"/> :
        <input type={type} value={v || ""} onChange={e => on(e.target.value)}
          className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px]"/>}
    </div>
  );
}
